import { useQuery } from '@tanstack/react-query';
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { TimerFab } from '../features/timesheet/components/timer-fab';
import { GuidedTour } from '../features/tour/components/guided-tour';
import { PersonalOrgPromptDialog } from '../features/organizations/components/personal-org-decision';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AbilitySummary } from '@apm/contracts';
import { AppShell } from '../components/layout/app-shell';
import { useAuth } from '../features/auth/auth-context';
import { RestructuringScreen } from '../features/org-chart/restructure/components/restructuring-screen';
import { apiFetch } from '../lib/http';
import { leadsAProject } from '../lib/org-role';
import { useOrgRestructuring } from '../lib/org-restructuring';
import { useOrgTimezoneSync } from '../lib/org-timezone';
import { LIMITED_SHELL_HOME, limitedShellAllows, shellModeOf } from '../lib/shell-mode';
import { useNotificationStream } from '../lib/use-notification-stream';

/** Trang giới thiệu cho khách chưa đăng nhập — tệp tĩnh ở `apps/web/public/landing.html`. */
const LANDING_PAGE_URL = '/landing.html';

/**
 * Route layout (pathless) bọc mọi màn hình cần đăng nhập.
 *
 * Chặn ở component chứ không ở `beforeLoad`: phiên được khôi phục **bất đồng
 * bộ** lúc mở trang (đổi refresh cookie lấy access token). `beforeLoad` chạy
 * trước khi việc đó xong, nên nó sẽ đá người dùng đang đăng nhập ra màn hình
 * login mỗi lần F5 — đúng cái mà APM-104 phải tránh.
 */
export const Route = createFileRoute('/_app')({ component: AppLayout });

function AppLayout(): ReactNode {
  const { t } = useTranslation();
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();
  /*
    Two paths for the limited-shell guard below — the **target** and what is **on screen**.

    - Target: `location` is the target as soon as a navigation starts, but it only
      counts when the target is an `_app` route. `/work`, `/login`, `/portal`,
      `/guest-invite/…` live outside `_app`; judging them by the list below would turn
      the redirect of `/work?task=…` into a bare `/projects`.
    - On screen: while route chunks load lazily `<Outlet />` still renders the **old**
      matches. Gating on the target alone let `/` → `/projects` mount the dashboard for
      a moment, and its own redirect then sent a guest to `/my-tasks`.
  */
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const targetPathname = useMemo(
    () =>
      router.getMatchedRoutes(pathname)[0].some((route) => route.id === '/_app') ? pathname : null,
    [router, pathname],
  );
  const shownPathname = useRouterState({
    select: (state) => state.matches.at(-1)?.pathname ?? state.location.pathname,
  });

  /**
   * Khách hàng đi thẳng sang portal — APM-ISSUE-n.
   *
   * `_app` dựng `AppShell` với menu điều hướng nội bộ, `TimerFab` và
   * `GuidedTour` — cả ba nói về một sản phẩm mà khách hàng không dùng. Không có
   * chuyển hướng này thì họ đăng nhập xong và thấy một khung ứng dụng gần như
   * trống rỗng, rồi tự kết luận rằng tài khoản của mình hỏng.
   *
   * ⚠️ Đây là **trải nghiệm**, không phải cửa quyền. Cửa thật nằm ở server: mọi
   * route nội bộ đã trả 403 cho vai này (`client-role.e2e-spec.ts`). Một chuyển
   * hướng ở frontend không bao giờ được là thứ duy nhất giữ dữ liệu.
   */
  useEffect(() => {
    if (status === 'authenticated' && user?.orgRole === 'CLIENT') {
      // Link thông báo issue (APM-ISX-c) trỏ `/issues?issue=…` — mang `issue` sang portal.
      const issue = new URLSearchParams(window.location.search).get('issue');
      void navigate({ to: '/portal', replace: true, ...(issue ? { search: { issue } } : {}) });
    }
  }, [status, user, navigate]);

  useEffect(() => {
    if (status !== 'anonymous') return;
    // Mang theo đường đang mở: người bấm link mời (APM-1317b) tới đây trước khi
    // đăng nhập, và mất token trong URL nghĩa là mất luôn lời mời.
    const next = `${window.location.pathname}${window.location.search}`;
    if (next === '/') {
      /*
        Khách lạ gõ đúng tên miền thì thấy trang giới thiệu, không phải ô đăng nhập.

        `landing.html` là tệp tĩnh trong `public/` (không qua React Router) nên
        phải đổi cả tài liệu — `navigate()` chỉ đổi URL trong SPA. Chỉ áp cho
        `/`: một link sâu (`/projects?…`, link mời) vẫn về `/login` kèm `next`,
        vì người đó đến để làm việc chứ không phải để đọc giới thiệu.
      */
      window.location.replace(LANDING_PAGE_URL);
      return;
    }
    void navigate({ to: '/login', replace: true, search: { next } });
  }, [status, navigate]);

  // Quyền dùng để ẩn/hiện menu. `enabled` để không bắn query khi chưa có token.
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    enabled: status === 'authenticated',
    staleTime: 5 * 60_000,
  });

  useNotificationStream(status === 'authenticated');
  // "Hôm nay" của cả giao diện là hôm nay của tổ chức — APM-TZ-i.
  useOrgTimezoneSync(status === 'authenticated');
  // Tổ chức đang gộp / tách (423) — APM-ORGMS-f: màn hình chờ thay cho nội dung trang.
  const restructuring = useOrgRestructuring();

  /*
    Limited shell (personal space, or a `GUEST` session) — APM-PSPACE-g, plan §3.4.

    `/` and every route outside the short list go to `/projects`. Decided here,
    before `<Outlet />`, so the page being left never mounts: mounting it for one
    frame is enough to fire its queries, and each of those is a red 403.
    `CLIENT` never reaches this point with a shell — the effect above sends it
    to `/portal`, and `limited` does not include it.
  */
  const limited = user ? shellModeOf(user) === 'limited' : false;
  const guarding = status === 'authenticated' && limited;
  const leaveRoute = guarding && targetPathname !== null && !limitedShellAllows(targetPathname);
  const hideOutlet = leaveRoute || (guarding && !limitedShellAllows(shownPathname));
  useEffect(() => {
    if (leaveRoute) void navigate({ to: LIMITED_SHELL_HOME, replace: true });
  }, [leaveRoute, navigate]);

  // `CLIENT` is on its way to `/portal`: no `AppShell` (it calls org-wide APIs
  // that answer 403 for that role, each one a red card while it waits).
  if (status !== 'authenticated' || !user || user.orgRole === 'CLIENT' || hideOutlet) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-muted-foreground">
        {status === 'restoring' ? t('auth.restoringSession') : t('common.loading')}
      </div>
    );
  }

  return (
    <AppShell
      user={user}
      permissions={abilities.data?.global ?? []}
      // Cùng hàm với chính trang Tổng quan (`canUseDashboard`): mục điều hướng
      // không được dẫn tới một trang tự đá người dùng đi.
      leadsProject={leadsAProject(abilities.data)}
      hasDirectReports={abilities.data?.hasDirectReports ?? false}
      onSignOut={() => void logout()}
    >
      {restructuring ? <RestructuringScreen /> : <Outlet />}
      {/* Sống ở layout, không ở một trang: spec mục 13 đòi cập nhật được "từ
          bất kỳ đâu", kể cả khi đang xem báo cáo. */}
      <TimerFab />
      {/* Tour sống ở layout vì nó dẫn người dùng **đi qua** nhiều màn hình —
          đặt trong một trang thì nó tự huỷ ngay ở chặng thứ hai (APM-1102). */}
      {/* Not in the limited shell: its steps walk the full bar (performance, OKR, the
          "Tính năng" menu) and would point at items that are not there. */}
      {limited ? null : <GuidedTour />}
      {/* Giữ / xoá tổ chức chỉ có mình tôi sau khi vào tổ chức khác — APM-PSPACE-n. */}
      <PersonalOrgPromptDialog user={user} />
    </AppShell>
  );
}
